// v49.2 — App shell.
// AuthProvider wraps the whole tree. /signin is the only public route;
// every other route is gated by AuthGate (redirects anonymous users +
// preserves the attempted path so SignInPage bounces them back after
// auth). The protected shell composes Sidebar (role-aware nav +
// profile dropdown) + Topbar (page title + active tenant pill) +
// the route's main content.

import { lazy as reactLazy, Suspense, useEffect, useRef, useState, type ComponentType, type LazyExoticComponent } from "react";
import { SOON_ROUTES } from "./features/shell/railData";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router";
import { useDocumentTitle, titleForPath } from "./lib/useDocumentTitle";
import "./lib/api";      /* side-effects: expose LABOURCORE_API on window */
import "./lib/crypto";   /* side-effects: init CRYPTO + expose on window */
import "./lib/db";       /* side-effects: expose TT_DB stub on window    */
import "./features/auth/helpers"; /* side-effects: window.shellSignOut + lcClearEncryptedTables + bootstrap auto-cleanup */
import { initErrorReporter } from "./lib/errorReporter";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { OfflineGate } from "./components/OfflineGate";

/* Wire global window.onerror + unhandledrejection listeners before
   anything else mounts — that way a render-time crash during AuthGate
   still lands in client_errors. */
initErrorReporter();

/* v50.240 — lazy() wrapper with automatic chunk-reload-on-failure.
   Classic React.lazy + CDN deploy pattern: when a deploy rotates the
   asset hashes, a user with a still-loaded index.html will try to
   fetch a not-yet-loaded route chunk by its OLD hash. The CDN returns
   404 (or wrong MIME) and the import() rejects. Previously this
   tripped the ErrorBoundary and the user had to click Reload.
   Now we catch the load failure, force a hard reload, and the fresh
   HTML brings down the current chunk references.

   The sessionStorage timestamp prevents infinite reload loops when
   the chunk is genuinely broken (not just stale) — at most one
   auto-reload per 60-second window per tab. */
function lazy<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return reactLazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const key = "lc-chunk-reload-at";
      const last = Number(window.sessionStorage.getItem(key) || "0");
      const now = Date.now();
      if (now - last > 60_000) {
        try { window.sessionStorage.setItem(key, String(now)); } catch { /* private mode */ }
        console.warn("[lazy] chunk load failed, forcing hard reload to pick up fresh asset hashes", err);
        window.location.reload();
        /* Return a never-resolving promise so React keeps the Suspense
           boundary in its loading state until the reload kicks in. */
        return new Promise<{ default: T }>(() => {});
      }
      /* Already attempted a reload recently — propagate so the
         ErrorBoundary catches it and the user can decide what to do. */
      throw err;
    }
  });
}
import { AuthProvider, useShellUser } from "./features/auth/AuthContext";
import { AuthGate } from "./features/auth/components/AuthGate";
import { ShellNotificationToaster } from "./features/mail/ShellNotificationToaster";
/* v50.300 — Dashboard Remodel 08-06 Part 2: global daily sign-off
   chase banner. Mounts in ProtectedShell so it persists across every
   route. Per the brief: "this is what makes it a chase rather than a
   dashboard widget the user can navigate away from". */
import { GlobalSignOffBanner } from "./features/_stubs/GlobalSignOffBanner";
import { AdminGate, adminTierOf } from "./features/admin/components/AdminGate";
import { AccountSetupPrompt } from "./features/auth/components/AccountSetupPrompt";
import { MfaGate } from "./features/auth/components/MfaChallenge";
import { useTrackRecentNav } from "./lib/useRecentNav";
import { useBreakpoint } from "./lib/useBreakpoint";
import { Sidebar } from "./features/shell/components/Sidebar";
import { Topbar }  from "./features/shell/components/Topbar";
import { MobileShellHeader } from "./features/shell/components/MobileShellHeader";
import { MobileBottomNav }   from "./features/shell/components/MobileBottomNav";
import { MobileNavSheet }    from "./features/shell/components/MobileNavSheet";
import { MobileFabSheet }    from "./features/shell/components/MobileFabSheet";
import { SearchPalette }    from "./features/shell/components/SearchPalette";
import { HelpWidget }       from "./features/shell/components/HelpWidget";
import { ToastProvider } from "./components/Toast";
import { ConfirmProvider } from "./components/Confirm";
import { CryptoWalletProvider } from "./features/crypto/CryptoWalletProvider";

const LandingPage  = lazy(() => import("./features/landing/LandingPage"));
const Home         = lazy(() => import("./features/_stubs/Home"));
const SignInPage   = lazy(() => import("./features/auth/SignInPage"));
const ForgotPasswordPage = lazy(() => import("./features/auth/ForgotPasswordPage"));
const ResetPasswordPage  = lazy(() => import("./features/auth/ResetPasswordPage"));
const Requirements = lazy(() => import("./features/requirements/RequirementsPage"));
const DealBoard    = lazy(() => import("./features/deals/DealBoard"));
const ActiveDeals  = lazy(() => import("./features/deals/ActiveDealsPage"));
const DealProfile  = lazy(() => import("./features/deals/DealProfilePage"));
const Inbox        = lazy(() => import("./features/mail/InboxPage"));
const Notifications = lazy(() => import("./features/mail/NotificationsPage"));
const Projects     = lazy(() => import("./features/projects/ProjectsPage"));
const ProjectProfile = lazy(() => import("./features/projects/ProjectProfilePage"));
/* v50.387 — Designer Role: global /designs page (coord-side sidebar). */
const DesignsPage    = lazy(() => import("./features/projects/DesignsPage"));
const NewProjectWizard = lazy(() => import("./features/projects/new-project-wizard/NewProjectWizard"));
const SiteOps        = lazy(() => import("./features/projects/SiteOpsPage"));
const ProjectsCalendar = lazy(() => import("./features/projects/CalendarPage"));
const Futures      = lazy(() => import("./features/futures/FuturesPage"));
const MyCrew       = lazy(() => import("./features/workers/MyCrewPage"));
const Availability = lazy(() => import("./features/workers/AvailabilityPage"));
const WorkerProfile = lazy(() => import("./features/workers/WorkerProfilePage"));
const SupplierPool   = lazy(() => import("./features/partners/SupplierPoolPage"));
const FindLabourMap  = lazy(() => import("./features/partners/FindLabourMapPage"));
const PartnerProfile = lazy(() => import("./features/partners/PartnerProfilePage"));
const CoordProfile = lazy(() => import("./features/coords/CoordProfilePage"));
const PublicPartnerProfile = lazy(() => import("./features/partners/PublicPartnerProfilePage"));
const PublicComplianceWallet = lazy(() => import("./features/compliance/PublicComplianceWalletPage"));
const PublicMarketReport = lazy(() => import("./features/reports/PublicMarketReportPage"));
const FeeCalculator = lazy(() => import("./features/calculator/FeeCalculatorPage"));
const Developers = lazy(() => import("./features/developers/DevelopersPage"));
const Compliance   = lazy(() => import("./features/compliance/ComplianceWalletPage"));
const ComplianceDocuments = lazy(() => import("./features/compliance/ComplianceDocumentsPage"));
const Insights     = lazy(() => import("./features/insights/InsightsPage"));
const Payments     = lazy(() => import("./features/payments/PaymentsPage"));
const MyFrameworks = lazy(() => import("./features/frameworks/MyFrameworksPage"));
const Earnings     = lazy(() => import("./features/earnings/EarningsPage"));
const Disputes     = lazy(() => import("./features/disputes/DisputesPage"));
const Settings     = lazy(() => import("./features/settings/SettingsPage"));
const SettingsLegal = lazy(() => import("./features/settings/SettingsLegalPage"));
const SettingsTeam  = lazy(() => import("./features/settings/SettingsTeamPage"));
const UserProfile  = lazy(() => import("./features/profile/UserProfilePage"));
const InvitationClaimPage = lazy(() => import("./features/auth/InvitationClaimPage"));
const CoordInviteClaimPage = lazy(() => import("./features/auth/CoordInviteClaimPage"));
const BYOPClaimPage = lazy(() => import("./features/auth/BYOPClaimPage"));
/* v50.351 Retention B10 — mobile-first single-action screens. Bare shell
   (no sidebar) at /m/*. ProtectedShell still wraps for auth, but the
   inner screens render their own MobileShell with a minimal header. */
const MobileProposalReview = lazy(() => import("./features/mobile/MobileProposalReview"));
const MobileLogConfirm     = lazy(() => import("./features/mobile/MobileLogConfirm"));
const MobileComplianceFlag = lazy(() => import("./features/mobile/MobileComplianceFlag"));
const YearInReviewPage     = lazy(() => import("./features/insights/YearInReviewPage"));
const RoadmapPage          = lazy(() => import("./features/roadmap/RoadmapPage"));
const AdminRoadmapPage     = lazy(() => import("./features/admin/AdminRoadmapPage"));
const AdminHyperscalePage  = lazy(() => import("./features/admin/AdminHyperscalePage"));
const AdminPipelinePage    = lazy(() => import("./features/admin/AdminPipelinePage"));
const AdminPreferredPartnersPage = lazy(() => import("./features/admin/AdminPreferredPartnersPage"));
const AdminInsightsSubscriptionsPage = lazy(() => import("./features/admin/AdminInsightsSubscriptionsPage"));
const InsightsProductPage = lazy(() => import("./features/insights/InsightsProductPage"));
const EnterprisePage = lazy(() => import("./features/enterprise/EnterprisePage"));
const AdminEnterpriseLeadsPage = lazy(() => import("./features/admin/AdminEnterpriseLeadsPage"));
const MarketModelPage = lazy(() => import("./features/admin/models/MarketModelPage"));
const CohortModelPage = lazy(() => import("./features/admin/models/CohortModelPage"));
const AdminCoveragePage = lazy(() => import("./features/admin/AdminCoveragePage"));
const AdminAuditEngagementsPage = lazy(() => import("./features/admin/AdminAuditEngagementsPage"));
const Ir35GuidePage        = lazy(() => import("./features/ir35/Ir35GuidePage"));
const PublicScorePage = lazy(() => import("./features/reliability/PublicScorePage"));
const ReliabilityMethodPage = lazy(() => import("./features/reliability/ReliabilityMethodPage"));
const CommunityPage = lazy(() => import("./features/community/CommunityPage"));
const CommunityThreadPage = lazy(() => import("./features/community/CommunityThreadPage"));
const HyperscaleMethodPage = lazy(() => import("./features/hyperscale/HyperscaleMethodPage"));
const OnboardingWizard = lazy(() => import("./features/onboarding/OnboardingWizardPage"));
const PartnerOriginationWizard = lazy(() => import("./features/origination/PartnerOriginationWizard"));
const TenderListPage    = lazy(() => import("./features/tenders/TenderListPage"));
const TenderDetailPage  = lazy(() => import("./features/tenders/TenderDetailPage"));
const MyBidsPage        = lazy(() => import("./features/tenders/MyBidsPage"));
const MyPostedTendersPage = lazy(() => import("./features/tenders/MyPostedTendersPage"));
const TenderPackAssemblyPage = lazy(() => import("./features/tenders/TenderPackAssemblyPage"));
const BidPackPrintView       = lazy(() => import("./features/tenders/BidPackPrintView"));
const TenderCreatePage       = lazy(() => import("./features/tenders/TenderCreatePage"));
const PostTenderWizard       = lazy(() => import("./features/tenders/post-tender-wizard/PostTenderWizard"));
const AdminDashboardPage   = lazy(() => import("./features/admin/AdminDashboardPage"));
const AdminOnboardingPage = lazy(() => import("./features/admin/AdminOnboardingPage"));
const AdminOriginationsPage = lazy(() => import("./features/admin/AdminOriginationsPage"));
const AdminCompaniesPage    = lazy(() => import("./features/admin/AdminCompaniesPage"));
const AdminAccessPage       = lazy(() => import("./features/admin/AdminAccessPage"));
const ComingSoon            = lazy(() => import("./features/shell/components/ComingSoon"));
const OpportunitiesPage     = lazy(() => import("./features/opportunities/OpportunitiesPage"));
const FindOpportunities     = lazy(() => import("./features/opportunities/FindOpportunitiesPage"));
const OpportunityBrief      = lazy(() => import("./features/opportunities/OpportunityBriefPage"));
const RequirementDetail     = lazy(() => import("./features/requirements/RequirementDetailPage"));
const FindLabourPage        = lazy(() => import("./features/requirements/FindLabourPage"));
const AdminCitiesPage       = lazy(() => import("./features/admin/AdminCitiesPage"));
const AdminEmailsPage       = lazy(() => import("./features/admin/AdminEmailsPage"));
const AdminBillingPage      = lazy(() => import("./features/admin/AdminBillingPage"));
const InvestorOverviewPage  = lazy(() => import("./features/admin/InvestorOverviewPage"));
const AdminMarketReportPage = lazy(() => import("./features/admin/AdminMarketReportPage"));
const AdminReferenceReviewPage = lazy(() => import("./features/admin/AdminReferenceReviewPage"));
const AuditLog     = lazy(() => import("./features/audit/AuditLogPage"));

/* v49.10.8 — if the signed-in user has no tenant (signup didn't
   complete — e.g. ran during the GoTrue lock outage), prompt them to
   finish setup before showing any tenant-scoped page. The
   AccountSetupPrompt does a profile upsert + create_company_for_user
   RPC, then reloads. */
function TenantGate({ children }: { children: React.ReactNode }) {
  const user = useShellUser();
  const location = useLocation();
  if (user && !user.companyId) {
    /* v50.321 — platform-admin logins (admin@/board@/investors@) have no
       tenant on purpose; don't prompt them to create a company.
       v50.324 — master/board may roam the whole platform (tenant-scoped
       pages render empty via RLS; the redirect-to-/admin trap made
       mobile, whose bottom tabs all point at tenant pages, feel stuck).
       Investor stays confined to the overview by design. */
    const tier = adminTierOf(user);
    if (tier === "investor" && !location.pathname.startsWith("/admin")) {
      return <Navigate to="/admin/investor" replace />;
    }
    if (tier) return <>{children}</>;
    return <AccountSetupPrompt />;
  }
  return <>{children}</>;
}

/* v49.24 — record every protected route visit into the recent-nav list. */
function RecentNavTracker() {
  useTrackRecentNav();
  return null;
}

/* Protected shell — gated by AuthGate. Layout swaps by breakpoint:
     • mobile (< 640px)  → MobileShellHeader + content + MobileBottomNav
                            + bottom sheets for FAB / More
     • tablet + desktop → Sidebar + Topbar + content (always static)
   The SearchPalette is mounted globally and listens for Cmd-K / Ctrl-K. */
function ProtectedShell({ children }: { children: React.ReactNode }) {
  const breakpoint = useBreakpoint();
  const [fabOpen, setFabOpen]     = useState(false);
  const [moreOpen, setMoreOpen]   = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const mainRef = useRef<HTMLElement | null>(null);

  /* Global Cmd-K / Ctrl-K listener. Anything inside an input still
     pre-empts the shortcut — we only intercept the bare key combo. */
  useGlobalSearchShortcut(() => setSearchOpen(true));

  /* v50.178 — WCAG 2.2 AA focus management on route change. When the
     pathname changes, programmatically move focus to the <main> region
     so keyboard / screen-reader users land at the new page's content
     instead of stranded on the just-clicked sidebar link. Skipped on
     first mount so we don't steal focus from a freshly-loaded page. */
  useRouteChangeFocus(mainRef);

  /* v50.180 — Page-level <title> per route. Sets a sensible default
     browser-tab label on every navigation. Individual pages can call
     useDocumentTitle to override with a row-specific name (e.g. the
     deal page setting "Deal — Slough DC2"). */
  const location = useLocation();
  useDocumentTitle(titleForPath(location.pathname));

  return (
    <AuthGate>
      <MfaGate>
       <TenantGate>
        <RecentNavTracker />
        {/* v50.178 — Skip-to-content link. First element in tab order on
            every protected page. Visually hidden until focused, then
            floats above all chrome so a keyboard user can jump past the
            sidebar / topbar repetition. */}
        <a
          href="#lc-main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[1100] focus:px-4 focus:py-2 focus:rounded-lc-md focus:bg-lc-blue focus:text-white focus:font-extrabold focus:text-sm focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
        >
          Skip to main content
        </a>
        {/* v50.14 — realtime notification toaster. Self-mounts on
            auth-resolved; fires a toast for every new placement_notifications
            row from this moment forward. */}
        <ShellNotificationToaster />
        {breakpoint === "mobile" ? (
          <div className="min-h-screen flex flex-col">
            <MobileShellHeader onOpenSearch={() => setSearchOpen(true)} />
            <GlobalSignOffBanner />
            <main
              ref={mainRef}
              id="lc-main-content"
              tabIndex={-1}
              className="flex-1 min-w-0 outline-none"
              style={{ paddingBottom: "calc(72px + env(safe-area-inset-bottom, 0px))" }}
            >
              {children}
            </main>
            <MobileBottomNav
              onOpenFab={() => setFabOpen(true)}
              onOpenMore={() => setMoreOpen(true)}
            />
            <MobileFabSheet open={fabOpen}   onClose={() => setFabOpen(false)} />
            <MobileNavSheet open={moreOpen}  onClose={() => setMoreOpen(false)} />
          </div>
        ) : (
          <div className="flex min-h-screen">
            <Sidebar />
            <div className="flex-1 min-w-0 flex flex-col">
              <Topbar onOpenSearch={() => setSearchOpen(true)} />
              <GlobalSignOffBanner />
              <main
                ref={mainRef}
                id="lc-main-content"
                tabIndex={-1}
                className="flex-1 min-w-0 outline-none"
              >
                {children}
              </main>
            </div>
          </div>
        )}
        <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
        <HelpWidget />
       </TenantGate>
      </MfaGate>
    </AuthGate>
  );
}

/* v50.178 — Move focus to the <main> region whenever the route changes.
   Skipped on first mount so a fresh pageload doesn't steal focus from
   the user's natural starting point (typically the URL bar). */
function useRouteChangeFocus(ref: React.RefObject<HTMLElement | null>) {
  const location = useLocation();
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current) { firstRenderRef.current = false; return; }
    if (ref.current) {
      try { ref.current.focus({ preventScroll: true }); } catch { /* ignore */ }
    }
  }, [location.pathname, ref]);
}

/* Cmd-K / Ctrl-K → trigger global search. Pressing it from inside an
   input still works (Stripe, Linear and Notion all behave this way). */
function useGlobalSearchShortcut(onTrigger: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key?.toLowerCase();
      if (k === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onTrigger();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onTrigger]);
}

export default function App() {
  return (
   <ErrorBoundary>
    <OfflineGate>
    <AuthProvider>
     <CryptoWalletProvider>
     <ToastProvider>
      <ConfirmProvider>
      <BrowserRouter>
        <Suspense fallback={<div className="p-10 text-sm text-lc-mute">Loading…</div>}>
          <Routes>
            {/* Public — marketing + auth + shareable partner profiles. */}
            <Route path="/"        element={<LandingPage />} />
            {/* v50.420 — Landing Overhaul 1: three role-led pages from one
                parameterised LandingPage (audience derived from the path).
                /coordinators kept as a redirect so existing links/ads survive. */}
            <Route path="/for/coordinators" element={<LandingPage />} />
            <Route path="/for/partners"     element={<LandingPage />} />
            <Route path="/for/hybrid"       element={<LandingPage />} />
            <Route path="/coordinators" element={<Navigate to="/for/coordinators" replace />} />
            <Route path="/signin"  element={<SignInPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password"  element={<ResetPasswordPage />} />
            <Route path="/p/:slug" element={<PublicPartnerProfile />} />
            {/* v50.329 — Defence P3: tokenised public compliance wallet.
                Anonymous by design; the RPC validates + rate-limits. */}
            <Route path="/cw/:token" element={<PublicComplianceWallet />} />
            {/* v50.331 — Defence P5: public quarterly market-data snapshot.
                Anonymous; the anon RPC only serves PUBLISHED, already
                cohort-suppressed aggregates. */}
            <Route path="/market-report" element={<PublicMarketReport />} />
            {/* v50.334 — Defence P8: public fee calculator. Anonymous; reads
                only the anon config RPC (live ladder, no per-entity data). */}
            <Route path="/fee-calculator" element={<FeeCalculator />} />
            {/* v50.337 — Defence P11: public developer docs for the
                read-only open API. Pure static reference — no data. */}
            <Route path="/developers" element={<Developers />} />
            {/* v50.108 — invitation claim. Public so signed-out
                visitors land here from the email and get the
                "sign in to claim" UX; the RPC requires auth. */}
            <Route path="/invitations/claim" element={<InvitationClaimPage />} />
            <Route path="/coord-invite/claim" element={<CoordInviteClaimPage />} />
            <Route path="/byop-invite/claim"  element={<BYOPClaimPage />} />
            {/* v50.351 Retention B10 — mobile-first workflow screens. */}
            <Route path="/m/proposal/:id"           element={<ProtectedShell><MobileProposalReview /></ProtectedShell>} />
            <Route path="/m/log/:logId"             element={<ProtectedShell><MobileLogConfirm /></ProtectedShell>} />
            <Route path="/m/flag/:placementId"      element={<ProtectedShell><MobileComplianceFlag /></ProtectedShell>} />
            <Route path="/score/:token" element={<PublicScorePage />} />
            <Route path="/reliability-method" element={<ReliabilityMethodPage />} />
            <Route path="/community"          element={<ProtectedShell><CommunityPage /></ProtectedShell>} />
            <Route path="/community/:id"      element={<ProtectedShell><CommunityThreadPage /></ProtectedShell>} />
            <Route path="/hyperscale-method"  element={<HyperscaleMethodPage />} />
            {/* v50.361 — M2 IR35 plain-English guide. Public route per the brief. */}
            <Route path="/ir35-guide"         element={<Ir35GuidePage />} />
            {/* v50.364 — M8 paid insights product marketing page. */}
            <Route path="/insights-product"   element={<InsightsProductPage />} />
            {/* v50.369 — M4 enterprise capabilities + data-partnership lead form. */}
            <Route path="/enterprise"         element={<EnterprisePage />} />

            {/* Protected — sidebar + topbar + main content. */}
            {/* v50.212 — nav redesign Phase 1: redirects for the new
                canonical top-level paths declared in NAV_AREAS. The
                existing pages stay at their legacy URLs; these
                redirects let the rail's NavLinks point to the brief's
                paths without inventing new screens. */}
            {/* /today IS the route now, not a hop to it.
                The rail says "Today" on four of the five tiers and pointed at
                /today, which redirected to /dashboard — so every landing and
                every click mounted the page, unmounted it and mounted it again.
                That double render is the jitter. The redirect is reversed:
                /dashboard still resolves for old links, but nothing in the nav
                travels through it. */}
            <Route path="/find-labour"  element={<Navigate to="/partners"     replace />} />
            <Route path="/find-work"    element={<Navigate to="/requirements" replace />} />
            <Route path="/my-crew"      element={<Navigate to="/workers"      replace />} />
            {/* v50.396 — Move 3: /track-record alias for the Tried &
                Tested surface. Dashboard tiles + partner track-record
                strip can link to a clean URL instead of a query string. */}
            <Route path="/track-record" element={<Navigate to="/partners?tab=tried-tested" replace />} />
            {/* v50.225 — Phase 3 URL-path migration: brief §3 sub-tab
                canonical paths now resolve to the existing query-param
                routes via Navigate redirects. Same pattern as the
                Phase 1 top-level redirects above. Adding a redirect
                instead of refactoring SettingsPage / ComplianceWallet
                / PaymentsPage keeps the migration light-touch — pages
                continue reading ?tab=X / ?filter=X internally; the
                rail just clicks brief paths. */}
            {/* Compliance sub-tabs. */}
            <Route path="/compliance/upload" element={<Navigate to="/compliance?section=upload"   replace />} />
            <Route path="/compliance/inbox"  element={<Navigate to="/compliance?section=inbox"    replace />} />
            <Route path="/compliance/expiry" element={<Navigate to="/compliance?section=expiry"   replace />} />
            <Route path="/compliance/certs"  element={<Navigate to="/compliance?section=verified" replace />} />
            {/* Payments sub-tabs. */}
            <Route path="/payments/invoices" element={<Navigate to="/payments?tab=invoices" replace />} />
            <Route path="/payments/fees"     element={<Navigate to="/payments?tab=fees"     replace />} />
            {/* Settings sub-tabs. */}
            <Route path="/settings/account"        element={<Navigate to="/settings?section=account"       replace />} />
            <Route path="/settings/security"       element={<Navigate to="/settings?section=security"      replace />} />
            <Route path="/settings/notifications"  element={<Navigate to="/settings?section=notifications" replace />} />
            <Route path="/settings/company"        element={<Navigate to="/settings?section=company"       replace />} />
            <Route path="/settings/billing"        element={<Navigate to="/settings?section=billing"       replace />} />
            <Route path="/settings/ai-matching"    element={<Navigate to="/settings?section=matching"      replace />} />
            <Route path="/settings/saved-searches" element={<Navigate to="/settings?section=searches"      replace />} />
            <Route path="/settings/data"           element={<Navigate to="/settings?section=data"          replace />} />
            <Route path="/settings/danger"         element={<Navigate to="/settings?section=danger"        replace />} />
            {/* Inbox sub-filters. */}
            <Route path="/inbox/unread"      element={<Navigate to="/inbox?filter=unread"      replace />} />
            <Route path="/inbox/invitations" element={<Navigate to="/inbox?filter=invitations" replace />} />
            <Route path="/inbox/responses"   element={<Navigate to="/inbox?filter=responses"   replace />} />
            <Route path="/inbox/sent"        element={<Navigate to="/inbox?filter=sent"        replace />} />
            <Route path="/inbox/by-partner"  element={<Navigate to="/inbox?filter=by_partner"  replace />} />
            <Route path="/inbox/by-stage"    element={<Navigate to="/inbox?filter=by_stage"    replace />} />
            <Route path="/today"         element={<ProtectedShell><Home /></ProtectedShell>} />
            <Route path="/dashboard"     element={<Navigate to="/today" replace />} />
            {/* FIND LABOUR — rebuilt against 05-post-requirement.html. The old
                "Live Requirements" list is gone; this is compose + review +
                the live stage the client designed. */}
            {/* CLIENT RAIL NAMES (Labourcore-Prototype/sandbox.js:80).
                Their Tier-1 Discover group is Find suppliers -> post-requirement,
                Entire pool list -> discovery. Registered as ALIASES rather than
                renames: /requirements has 43 internal references and /partners
                26, and a rename would break every one of them. The rail points
                at the new names; the old paths keep working. */}
            <Route path="/post-requirement" element={<ProtectedShell><FindLabourPage /></ProtectedShell>} />
            <Route path="/requirements"  element={<ProtectedShell><FindLabourPage /></ProtectedShell>} />
            {/* ACTIVE DEALS.
                /deals is the BOARD — four lanes, the shape the client's
                mockup and the rail both point at. The old filterable list is
                not deleted: it is where each lane's "+N more" goes, because a
                capped lane needs somewhere to send you for the rest.

                /deals/list is registered BEFORE /deals/:id or "list" would be
                matched as a deal id. */}
            <Route path="/deals"         element={<ProtectedShell><DealBoard /></ProtectedShell>} />
            <Route path="/deals/list"    element={<ProtectedShell><ActiveDeals /></ProtectedShell>} />
            <Route path="/deals/:id"     element={<ProtectedShell><DealProfile /></ProtectedShell>} />
            <Route path="/inbox"         element={<ProtectedShell><Inbox /></ProtectedShell>} />
            <Route path="/notifications" element={<ProtectedShell><Notifications /></ProtectedShell>} />
            <Route path="/projects"      element={<ProtectedShell><Projects /></ProtectedShell>} />
            <Route path="/designs"       element={<ProtectedShell><DesignsPage /></ProtectedShell>} />
            {/* v50.220 — Phase 2 net-new screen: Projects ▸ Site ops &
                diary. Static path must register BEFORE /projects/:id so
                /projects/site-ops doesn't get matched by the dynamic
                route as if "site-ops" were a project id. */}
            <Route path="/projects/site-ops" element={<ProtectedShell><SiteOps /></ProtectedShell>} />
            {/* v50.221 — Phase 2 net-new screen: Projects ▸ Calendar.
                Same /projects/:id collision concern — register before. */}
            <Route path="/projects/calendar" element={<ProtectedShell><ProjectsCalendar /></ProtectedShell>} />
            {/* v50.259 — six-step New-Project wizard. Static path, must
                register BEFORE /projects/:id to avoid collision. */}
            <Route path="/projects/new"      element={<ProtectedShell><NewProjectWizard /></ProtectedShell>} />
            <Route path="/projects/:id"  element={<ProtectedShell><ProjectProfile /></ProtectedShell>} />
            <Route path="/futures"       element={<ProtectedShell><Futures /></ProtectedShell>} />
            <Route path="/workers"      element={<ProtectedShell><MyCrew /></ProtectedShell>} />
            <Route path="/workers/:id"  element={<ProtectedShell><WorkerProfile /></ProtectedShell>} />
            {/* v50.229 — My crew ▸ Availability (partner-declared per-operative status). */}
            <Route path="/my-crew/availability" element={<ProtectedShell><Availability /></ProtectedShell>} />
            <Route path="/discovery"    element={<ProtectedShell><SupplierPool /></ProtectedShell>} />
            <Route path="/partners"     element={<ProtectedShell><SupplierPool /></ProtectedShell>} />
            {/* v50.219 — Phase 2 net-new screen: Find labour ▸ Map. */}
            <Route path="/find-labour/map" element={<ProtectedShell><FindLabourMap /></ProtectedShell>} />
            <Route path="/discovery/:id" element={<ProtectedShell><PartnerProfile /></ProtectedShell>} />
            <Route path="/partners/:id" element={<ProtectedShell><PartnerProfile /></ProtectedShell>} />
            <Route path="/coords/:id"   element={<ProtectedShell><CoordProfile /></ProtectedShell>} />
            <Route path="/compliance"   element={<ProtectedShell><Compliance /></ProtectedShell>} />
            {/* v50.218 — Phase 2 net-new screen: Compliance ▸ Documents archive. */}
            <Route path="/compliance/documents" element={<ProtectedShell><ComplianceDocuments /></ProtectedShell>} />
            <Route path="/insights"               element={<ProtectedShell><Insights /></ProtectedShell>} />
            <Route path="/insights/year-in-review" element={<ProtectedShell><YearInReviewPage /></ProtectedShell>} />
            {/* v50.353 — Retention B15: public roadmap (read-only for everyone, vote+comment advisory-only). */}
            <Route path="/roadmap"      element={<ProtectedShell><RoadmapPage /></ProtectedShell>} />
            <Route path="/payments"     element={<ProtectedShell><Payments /></ProtectedShell>} />
            {/* v50.328 — Defence P2: e-signed framework agreements. */}
            <Route path="/frameworks"   element={<ProtectedShell><MyFrameworks /></ProtectedShell>} />
            <Route path="/earnings"     element={<ProtectedShell><Earnings /></ProtectedShell>} />
            <Route path="/disputes"     element={<ProtectedShell><Disputes /></ProtectedShell>} />
            <Route path="/audit"        element={<ProtectedShell><AuditLog /></ProtectedShell>} />
            <Route path="/settings"     element={<ProtectedShell><Settings /></ProtectedShell>} />
            {/* v50.215 — Phase 2 net-new screen: Settings ▸ Legal. */}
            <Route path="/settings/legal" element={<ProtectedShell><SettingsLegal /></ProtectedShell>} />
            {/* v50.216 — Phase 2 net-new screen: Settings ▸ Team. */}
            <Route path="/settings/team"  element={<ProtectedShell><SettingsTeam /></ProtectedShell>} />
            <Route path="/me"           element={<ProtectedShell><UserProfile /></ProtectedShell>} />
            {/* v50.437 — rendered inside the app shell (sidebar/topbar
                visible) like /projects/new and /tenders/new, instead of
                a standalone full-screen takeover. Same route, no reroute. */}
            <Route path="/onboarding"   element={<ProtectedShell><OnboardingWizard /></ProtectedShell>} />
            {/* v49.67 — Move 1: partner-initiated package origination. */}
            <Route path="/originate"    element={<AuthGate><PartnerOriginationWizard /></AuthGate>} />
            {/* v49.85 — Move 3 scaffold: bid engine (tender list / detail / my bids). */}
            <Route path="/tenders"            element={<ProtectedShell><TenderListPage /></ProtectedShell>} />
            <Route path="/tenders/me"         element={<ProtectedShell><MyBidsPage /></ProtectedShell>} />
            <Route path="/tenders/posted"     element={<ProtectedShell><MyPostedTendersPage /></ProtectedShell>} />
            <Route path="/tenders/new"        element={<ProtectedShell><PostTenderWizard /></ProtectedShell>} />
            <Route path="/tenders/:id"        element={<ProtectedShell><TenderDetailPage /></ProtectedShell>} />
            <Route path="/tenders/:id/edit"   element={<ProtectedShell><TenderCreatePage /></ProtectedShell>} />
            <Route path="/tenders/:id/pack"   element={<ProtectedShell><TenderPackAssemblyPage /></ProtectedShell>} />
            <Route path="/tenders/:id/pack/print" element={<ProtectedShell><BidPackPrintView /></ProtectedShell>} />
            {/* v49.69 / v49.79 / v50.321 — admin back-office. AdminGate wraps
                AuthGate and now declares the allowed platform-admin tiers per
                route: master sees everything; board gets the view-only
                oversight set (no emails, no billing); investor only gets the
                revenue overview. */}
            <Route path="/admin"             element={<AdminGate tiers={["master","board"]}><ProtectedShell><AdminDashboardPage /></ProtectedShell></AdminGate>} />
            <Route path="/admin/onboarding"  element={<AdminGate tiers={["master","board"]}><ProtectedShell><AdminOnboardingPage /></ProtectedShell></AdminGate>} />
            {/* v49.77 — admin queue for partner-originated packages. */}
            <Route path="/admin/originations" element={<AdminGate tiers={["master","board"]}><ProtectedShell><AdminOriginationsPage /></ProtectedShell></AdminGate>} />
            <Route path="/admin/companies"   element={<AdminGate tiers={["master","board"]}><ProtectedShell><AdminCompaniesPage /></ProtectedShell></AdminGate>} />
            {/* The control plane. master only — board is read-only and this screen
                exists to change things. */}
            <Route path="/admin/access"      element={<AdminGate tiers={["master"]}><ProtectedShell><AdminAccessPage /></ProtectedShell></AdminGate>} />

            {/* Rail items the prototype describes and this product has not built
                yet. Registered from railData so the two cannot drift: adding a
                `soon` item there gives it a working route here automatically,
                and the rail never links to a 404. */}
            {/* The supplier's side of a requirement. Reached from the MEP
                rail's "My bids"; buyers never land here. */}
            <Route path="/opportunities"     element={<ProtectedShell><OpportunitiesPage /></ProtectedShell>} />
            {/* FIND OPPORTUNITIES — the market board, and the brief behind the
                NDA gate. Same rows as /opportunities, split by whether the
                supplier has signed yet: unsigned briefs are a DISCOVERY, signed
                ones are a PIPELINE, and those are two different jobs.

                The brief has its own URL rather than being a modal, so it can be
                sent to a colleague, linked from a notification, and survive a
                refresh. Static path registers BEFORE the dynamic one. */}
            <Route path="/find-opportunities"     element={<ProtectedShell><FindOpportunities /></ProtectedShell>} />
            <Route path="/find-opportunities/:id" element={<ProtectedShell><OpportunityBrief /></ProtectedShell>} />
            {/* The buyer's side: who the requirement went to, where each is,
                and booking the ones that opted in. */}
            <Route path="/requirements/:id"  element={<ProtectedShell><RequirementDetail /></ProtectedShell>} />

            {SOON_ROUTES.map(r => (
              <Route key={r.route} path={r.route}
                     element={<ProtectedShell><ComingSoon /></ProtectedShell>} />
            ))}
            <Route path="/admin/cities"      element={<AdminGate tiers={["master","board"]}><ProtectedShell><AdminCitiesPage /></ProtectedShell></AdminGate>} />
            <Route path="/admin/emails"      element={<AdminGate><ProtectedShell><AdminEmailsPage /></ProtectedShell></AdminGate>} />
            <Route path="/admin/billing"     element={<AdminGate><ProtectedShell><AdminBillingPage /></ProtectedShell></AdminGate>} />
            {/* v50.321 — investor lump-sum revenue overview. */}
            <Route path="/admin/investor"    element={<AdminGate tiers={["master","investor"]}><ProtectedShell><InvestorOverviewPage /></ProtectedShell></AdminGate>} />
            {/* v50.331 — Defence P5: quarterly market report pipeline. */}
            <Route path="/admin/market-report" element={<AdminGate><ProtectedShell><AdminMarketReportPage /></ProtectedShell></AdminGate>} />
            <Route path="/admin/reference-review" element={<AdminGate tiers={["master","board"]}><ProtectedShell><AdminReferenceReviewPage /></ProtectedShell></AdminGate>} />
            {/* v50.353 — Retention B15: admin roadmap CRUD + founding/advisory cohort manager. */}
            <Route path="/admin/roadmap"      element={<AdminGate><ProtectedShell><AdminRoadmapPage /></ProtectedShell></AdminGate>} />
            {/* v50.355 — Defence P17 follow-up: hyperscale per-field verify queue. */}
            <Route path="/admin/hyperscale"   element={<AdminGate tiers={["master","board"]}><ProtectedShell><AdminHyperscalePage /></ProtectedShell></AdminGate>} />
            {/* v50.360 — M7 pipeline curate + admin volume trend. */}
            <Route path="/admin/pipeline"     element={<AdminGate tiers={["master","board"]}><ProtectedShell><AdminPipelinePage /></ProtectedShell></AdminGate>} />
            {/* v50.362 — M5 preferred partners admin cohort manager. */}
            <Route path="/admin/preferred-partners" element={<AdminGate><ProtectedShell><AdminPreferredPartnersPage /></ProtectedShell></AdminGate>} />
            {/* v50.364 — M8 insights subscription manager. */}
            <Route path="/admin/insights"     element={<AdminGate><ProtectedShell><AdminInsightsSubscriptionsPage /></ProtectedShell></AdminGate>} />
            {/* v50.369 — M4 admin enterprise-leads queue. */}
            <Route path="/admin/enterprise-leads" element={<AdminGate><ProtectedShell><AdminEnterpriseLeadsPage /></ProtectedShell></AdminGate>} />
            {/* v50.370 — Investor Readiness Briefs 1+2 standalone models. */}
            <Route path="/admin/model/market" element={<AdminGate tiers={["master","investor"]}><ProtectedShell><MarketModelPage /></ProtectedShell></AdminGate>} />
            <Route path="/admin/model/cohort" element={<AdminGate tiers={["master","investor"]}><ProtectedShell><CohortModelPage /></ProtectedShell></AdminGate>} />
            {/* v50.372 — Investor Readiness I13 supply-coverage dashboard. */}
            <Route path="/admin/coverage" element={<AdminGate tiers={["master","board"]}><ProtectedShell><AdminCoveragePage /></ProtectedShell></AdminGate>} />
            {/* v50.375 — Investor Readiness I12 compliance-audit-as-a-service. */}
            <Route path="/admin/audit-engagements" element={<AdminGate tiers={["master","board"]}><ProtectedShell><AdminAuditEngagementsPage /></ProtectedShell></AdminGate>} />

            <Route path="*" element={
              <div className="p-10">
                <h1 className="text-2xl font-extrabold">404</h1>
                <p className="text-sm text-lc-mute mt-2">Route not found.</p>
              </div>
            } />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </ConfirmProvider>
     </ToastProvider>
     </CryptoWalletProvider>
    </AuthProvider>
    </OfflineGate>
   </ErrorBoundary>
  );
}
